import { percentOf } from "../percent";
import { seatAt, seatOfUnit, seatUnit, withSeat } from "../run/seats";
import type { RunRules, ValorRules } from "../types/rules";
import type { RunState, SeatId, SeatState, Unit, UnitKind } from "../types/state";
import { replaceUnit, unitStats } from "../units/unit";

/**
 * Valor (V1 of the Soldier's growth, docs/design/SOLDIER-GROWTH-IDEA.md section 5): the seat's
 * strength this run, its titles and what each title grants the seat's own unit, valor earned
 * and valor lost at a death. Every grant is 0 for any other unit and without `soldier.valor`.
 */

/** The valor block of the run's rules; undefined while it is off. */
export function valorRulesOf(rules: RunRules): ValorRules | undefined {
  return rules.soldier?.valor;
}

/** The last title: the Champion's, past which valor counts and grants nothing. */
export function topTitle(valor: ValorRules): number {
  return valor.hp.length - 1;
}

/** The valor title `title` takes (0 is Levy): `step` x L x (L + 3) / 2. */
export function titleThreshold(valor: ValorRules, title: number): number {
  return (valor.step * title * (title + 3)) / 2;
}

/** The title `amount` of valor holds. */
export function titleOfValor(valor: ValorRules, amount: number): number {
  let title = 0;
  while (title < topTitle(valor) && amount >= titleThreshold(valor, title + 1)) {
    title += 1;
  }
  return title;
}

/** The seat's valor; 0 without the block or the seat. */
export function valorOf(seat: SeatState | undefined): number {
  return seat?.valor ?? 0;
}

/** The seat's title now: Levy without the block. */
export function seatTitle(state: RunState, seat: SeatState | undefined): number {
  const valor = valorRulesOf(state.rules);
  return valor ? titleOfValor(valor, valorOf(seat)) : 0;
}

/** One title's entry of a grant table, 0 or false past its end. */
function grantOf<Grant>(table: readonly Grant[], title: number, none: Grant): Grant {
  return table[title] ?? none;
}

/** A grant of its seat's title for a seat's unit: `none` for any other unit and without the block. */
function titleGrant<Grant>(
  state: RunState,
  unit: Unit,
  pick: (valor: ValorRules) => readonly Grant[],
  none: Grant,
): Grant {
  const valor = valorRulesOf(state.rules);
  const seat = valor && seatOfUnit(state, unit.id);
  if (!valor || !seat) {
    return none;
  }
  return grantOf(pick(valor), titleOfValor(valor, valorOf(seat)), none);
}

/** A unit's full HP: its kind's, and for a seat's unit its title's on top. */
export function maxHpOf(state: RunState, unit: Unit): number {
  return unitStats(state.rules, unit.kind).hp + titleGrant(state, unit, (valor) => valor.hp, 0);
}

/** What a unit holds while dug in: its kind's hold, its seat's title's on top. */
export function holdOf(state: RunState, unit: Unit): number {
  return (
    unitStats(state.rules, unit.kind).holdBonus + titleGrant(state, unit, (valor) => valor.hold, 0)
  );
}

/** What a seat's title adds to its unit's attack in a blow and a parting blow; 0 for any other. */
export function titleAttack(state: RunState, unit: Unit): number {
  return titleGrant(state, unit, (valor) => valor.attack, 0);
}

/** What a seat's title adds to its unit's healing a production interval; 0 for any other. */
export function titleHeal(state: RunState, unit: Unit): number {
  return titleGrant(state, unit, (valor) => valor.heal, 0);
}

/** A seat's unit leaves a fight with no parting blow (the Captain's gift). */
export function leavesFree(state: RunState, unit: Unit): boolean {
  return titleGrant(state, unit, (valor) => valor.freeExit, false);
}

/** The HP the seat's soldier of `kind` is born with: its kind's, and its title's on top. */
export function bornHp(state: RunState, seat: SeatState, kind: UnitKind): number {
  const valor = valorRulesOf(state.rules);
  const title = valor ? grantOf(valor.hp, titleOfValor(valor, valorOf(seat)), 0) : 0;
  return unitStats(state.rules, kind).hp + title;
}

/** Where the seat stands on its climb: its title, the valor that took it and the next, and the share between. */
export interface ValorProgress {
  readonly title: number;
  readonly valor: number;
  readonly from: number;
  /** The next title's valor; absent at the last title. */
  readonly next?: number;
  /** 0 at `from`, 1 at `next`; 1 at the last title. */
  readonly share: number;
}

/** The seat's climb for the screen; undefined without the block or the seat. */
export function valorProgress(
  state: RunState,
  seat: SeatState | undefined,
): ValorProgress | undefined {
  const valor = valorRulesOf(state.rules);
  if (!valor || seat?.valor === undefined) {
    return undefined;
  }
  const amount = seat.valor;
  const title = titleOfValor(valor, amount);
  const from = titleThreshold(valor, title);
  if (title >= topTitle(valor)) {
    return { title, valor: amount, from, share: 1 };
  }
  const next = titleThreshold(valor, title + 1);
  return { title, valor: amount, from, next, share: Math.min(1, (amount - from) / (next - from)) };
}

/**
 * The seat earns `amount` of valor. Its peak follows. A title it reaches lifts its unit's HP with
 * the title's full HP, so a kill never leaves it nearer its break line; a title reached for the
 * first time this run also stamps the peak's tick and heals `secondWind` a title (the second
 * wind), and one reached again after a fall gives the lift alone.
 */
export function earnValor(state: RunState, seatId: SeatId, amount: number): RunState {
  const valor = valorRulesOf(state.rules);
  const seat = seatAt(state, seatId);
  if (!valor || !seat || amount <= 0) {
    return state;
  }
  const before = seat.valor ?? 0;
  const now = before + amount;
  const peak = seat.peakValor ?? 0;
  const peakTitle = titleOfValor(valor, peak);
  const title = titleOfValor(valor, now);
  const earned = withSeat(state, {
    ...seat,
    valor: now,
    peakValor: Math.max(peak, now),
    peakTick: title > peakTitle ? state.tick : (seat.peakTick ?? 0),
  });
  const unit = seatUnit(state, seat);
  const was = titleOfValor(valor, before);
  if (!unit || title <= was) {
    return earned;
  }
  const rise = grantOf(valor.hp, title, 0) - grantOf(valor.hp, was, 0);
  const wind = valor.secondWind * Math.max(0, title - peakTitle);
  const full = unitStats(state.rules, unit.kind).hp + grantOf(valor.hp, title, 0);
  const hp = Math.min(full, unit.hp + rise + wind);
  return hp === unit.hp ? earned : replaceUnit(earned, unit, { ...unit, hp });
}

/** The seat after its unit fell: `deathPercent` of its valor taken, rounded down. */
export function fallenSeat(rules: RunRules, seat: SeatState): SeatState {
  const valor = valorRulesOf(rules);
  if (!valor || seat.valor === undefined) {
    return seat;
  }
  return { ...seat, valor: seat.valor - percentOf(seat.valor, valor.deathPercent) };
}
